import {
  Album,
  Identifier,
  IdentifierOmittedType,
  IdentifierType,
} from '@hooks/useAlbumInfo/types';
import { getUriId } from '@helpers/utils';
import { EntityLink } from '../shared/EntityLinksModal/types';

export const buildReleaseLinksFromAlbumData = (albumData: Album) => {
  const artistReleaseLinks: EntityLink[] =
    albumData.artists?.map(artist => ({
      entity: artist.name,
      type: artist.role,
      uri: artist.uri,
      url: `https://open.spotify.com/artist/${getUriId(artist.uri)}`,
    })) || [];
  const trackReleaseLinks: EntityLink[] =
    albumData.tracks?.map(track => ({
      entity: track.name,
      type: 'Track',
      uri: track.uri,
      url: `https://open.spotify.com/track/${getUriId(track.uri)}`,
    })) || [];

  return [
    {
      entity: albumData.name,
      type: 'Release',
      uri: albumData.uri,
      url: albumData.url,
    },
    ...artistReleaseLinks,
    ...trackReleaseLinks,
  ];
};

const identifierUpcTypes: IdentifierType[] = [
  IdentifierType.EAN,
  IdentifierType.JAN,
  IdentifierType.UPC,
];

export const isIdentifierOmitted = (type: IdentifierOmittedType): boolean => {
  const identifierOmittedTypes: IdentifierOmittedType[] = [
    IdentifierOmittedType.FAKE_ID,
    IdentifierOmittedType.FILIPE_TEST_FEED_ID,
    IdentifierOmittedType.GMB,
    IdentifierOmittedType.GMB_ID,
    IdentifierOmittedType.SPOTIFY_ASIA,
    IdentifierOmittedType.SPOTIFY_ASIA_ID,
  ];

  return identifierOmittedTypes.includes(type as IdentifierOmittedType);
};

export const formatIdentifierType = (
  identifierType: string,
): IdentifierType | string => {
  const vendorIdVariations = ['vender_id', 'vendor_id', 'vendorid', 'vnder_id'];
  const lowerCaseIdentifierType = identifierType?.toLowerCase();

  if (vendorIdVariations.includes(lowerCaseIdentifierType)) {
    return IdentifierType.VENDOR_ID;
  }

  switch (lowerCaseIdentifierType) {
    case 'upc':
      return IdentifierType.UPC;
    case 'ean':
      return IdentifierType.EAN;
    case 'jan':
      return IdentifierType.JAN;
    case 'proprietaryid':
      return IdentifierType.PROPRIETARY_ID;
    case 'catalognumber':
      return IdentifierType.CATALOG_NUMBER;
    case 'grid':
      return IdentifierType.GRID;
    case 'isrc':
      return IdentifierType.ISRC;
    case 'iswc':
      return IdentifierType.ISWC;
    default:
      return identifierType;
  }
};

/*
  Primary Identifiers are chosen in the following order:
    1. Value does not exact match but contains Primary release ID with special character/separator
    2. UPC/EAN/JAN identifier with exact Primary release ID match
    3. Non-UPC/EAN/JAN identifier with exact Primary release ID match
*/
export const findPrimaryIdentifierMatch = (
  identifiers: Identifier[],
  primaryReleaseIdValue: string,
): Identifier | undefined => {
  let containsPrimaryReleaseIdAndSpecialChar: Identifier | null = null;
  let exactMatchUPCType: Identifier | null = null;
  let exactMatchNonUPCType: Identifier | null = null;

  identifiers?.forEach(identifier => {
    const { type, value } = identifier;
    if (value.includes(primaryReleaseIdValue)) {
      // regex checks if any non-letter or -number character in `value`
      if (value !== primaryReleaseIdValue && /[^a-zA-Z0-9\s]/.test(value)) {
        containsPrimaryReleaseIdAndSpecialChar = identifier;
      } else if (
        value === primaryReleaseIdValue &&
        identifierUpcTypes.includes(
          formatIdentifierType(type) as IdentifierType,
        )
      ) {
        exactMatchUPCType = identifier;
      } else if (value === primaryReleaseIdValue) {
        exactMatchNonUPCType = identifier;
      }
    }
  });

  if (containsPrimaryReleaseIdAndSpecialChar) {
    return containsPrimaryReleaseIdAndSpecialChar;
  } else if (exactMatchUPCType) {
    return exactMatchUPCType;
  } else if (exactMatchNonUPCType) {
    return exactMatchNonUPCType;
  }
  return undefined;
};

export const getOrderedReleaseIdentifiers = (
  identifiers: Identifier[],
  primaryReleaseIdValue: string,
  feedName: string,
): {
  primaryIdentifierType: string | undefined;
  orderedReleaseIdentifiers: Identifier[];
} => {
  const otherIdentifiers: Identifier[] = [];
  const validIdentifiers = identifiers?.filter(
    identifier =>
      !isIdentifierOmitted(identifier.type as IdentifierOmittedType) &&
      identifier.type !== feedName,
  );

  const foundPrimaryIdentifier: Identifier | null =
    findPrimaryIdentifierMatch(validIdentifiers, primaryReleaseIdValue) || null;
  const formattedFoundPrimaryIdentifier = foundPrimaryIdentifier && {
    type: formatIdentifierType(foundPrimaryIdentifier.type),
    value: foundPrimaryIdentifier.value,
  };

  validIdentifiers?.forEach(identifier => {
    const { type, value } = identifier;
    const formattedType = formatIdentifierType(type);
    const formattedIdentifier: Identifier = {
      type: formattedType,
      value,
    };

    // Save every identifier except for the found primary identifier
    if (formattedType !== formattedFoundPrimaryIdentifier?.type) {
      otherIdentifiers.push(formattedIdentifier);
    }
  });

  const sortReleaseIdentifiers = (identifierList: Identifier[]) => {
    return identifierList
      .filter(identifier => identifier && identifier.type !== undefined)
      .sort((identifier1, identifier2) => {
        const type1 = identifier1.type;
        const type2 = identifier2.type;

        if (identifierUpcTypes.includes(type1 as IdentifierType)) {
          return -1;
        } else if (identifierUpcTypes.includes(type2 as IdentifierType)) {
          return 1;
        }
        return type1.localeCompare(type2);
      });
  };

  const sortedIdentifiers = sortReleaseIdentifiers(otherIdentifiers);
  const orderedReleaseIdentifiers = [
    formattedFoundPrimaryIdentifier!,
    ...sortedIdentifiers,
  ];

  return {
    primaryIdentifierType: formattedFoundPrimaryIdentifier
      ? formattedFoundPrimaryIdentifier.type
      : undefined,
    orderedReleaseIdentifiers,
  };
};

export const splitToRows = <T>(identifiers: T[]) => {
  return identifiers.reduce((rows, identifier, index, arr) => {
    if (index % 2 === 0) {
      rows.push([identifier, arr[index + 1]].filter(Boolean));
    }
    return rows;
  }, [] as T[][]);
};
